import {
  Container,
  Typography,
  Paper,
  Select,
  FormControl,
  MenuItem,
  Button,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Box,
  TableHead,
  TableRow,
  Checkbox,
  CircularProgress,
  Snackbar,
  Alert,
  Fade,
  Tooltip,
  Divider,
  IconButton,
  Modal,
} from "@mui/material";
import React, { useState, useEffect, useCallback } from "react";
import { styled } from "@mui/material/styles";
import { useParams } from "react-router-dom";
import base64 from "base64-js";
import { Info as InfoIcon } from "@mui/icons-material";


const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  // borderRadius: 12,
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  background: "linear-gradient(145deg, #ffffff, #f9f9f9)",
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 20,
  padding: "8px 24px",
  textTransform: "none",
  fontWeight: 600,
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
}));

function ClassMapping() {
  const [canAdd, setCanAdd] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [ddlClass, setddlClass] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [subjectMapping, setSubjectMapping] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Info modal state
  const [showInfoModal, setShowInfoModal] = useState(false);

  const decodeFormId = (encodedFormId) => {
    if (!encodedFormId || typeof encodedFormId !== "string") {
      console.error("encodedFormId is missing or invalid:", encodedFormId);
      return null;
    }
    try {
      const paddedEncodedFormId = encodedFormId.padEnd(Math.ceil(encodedFormId.length / 4) * 4, "=");
      const bytes = base64.toByteArray(paddedEncodedFormId);
      return new TextDecoder().decode(bytes);
    } catch (error) {
      console.error("Error decoding formId:", error);
      return null;
    }
  };

  const { encodedFormId } = useParams();
  const formId = decodeFormId(encodedFormId);

  const Authorizer = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ formId, }), // replace with actual formId if hardcoded
      });

      const result = await response.json();
      const data = result[0];

      // setAuthorization(data);
      setCanEdit(data?.uModify === 1);
      setCanAdd(data?.uWrite === 1);
      setCanDelete(data?.uDelete === 1);
    } catch (error) {
      console.error("Authorization check failed:", error);
    }
  };

  const fetchddlClass = useCallback(async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/Enquiry/ddlClassName`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) throw new Error(`Error: ${response.status}`);
      const data = await response.json();
      if (data.data === null && data.msg === "Record Not Found") {
        throw new Error("Record Not Found");
      }
      setddlClass(data);
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: "Failed to load classes",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const handleClassChange = useCallback(async (event) => {
    const classId = event.target.value;
    setSelectedClassId(classId);
    if (!classId) {
      setSubjectMapping([]);
      return;
    }
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/ClassMapping/GetSubjectMapping`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ classId }),
      });
      if (!response.ok) throw new Error(`Error: ${response.status}`);
      const data = await response.json();
      if (data.data === null && data.msg === "Record Not Found") {
        throw new Error("Record Not Found");
      }
      setSubjectMapping(
        data.map((subject) => ({
          ...subject,
          checked: subject.status === "1" || false,
        }))
      );
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: "Failed to load subjects",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSave = useCallback(async () => {
    try {
      setLoading(true);
      const payload = subjectMapping
        .filter((subject) => subject.checked)
        .map((subject) => ({
          classId: selectedClassId,
          subjectId: subject.subjectId,
        }));

      if (payload.length === 0) {
        setSnackbar({
          open: true,
          message: "Please select at least one subject",
          severity: "warning",
        });
        return;
      }

      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(apiUrl + "/ClassMapping", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`Error: ${response.status}`);

      setSubjectMapping([]);
      setSelectedClassId("");
      setSnackbar({
        open: true,
        message: "Mapping saved successfully",
        severity: "success",
      });
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: "Failed to save mapping",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [subjectMapping, selectedClassId]);

  useEffect(() => {
    fetchddlClass();
    Authorizer();
  }, [fetchddlClass]);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Info sections data
  const infoSections = [
    {
      title: "📋 Page Overview",
      content:
        "The Class Mapping page allows authorized staff to assign subjects to a specific class. Select a class from the dropdown to load all available subjects, then check the subjects to be mapped and click SAVE MAPPING.",
    },
    {
      title: "🎓 How to Use",
      items: [
        "Step 1 – Select a class from the Class dropdown. The subject list will load automatically.",
        "Step 2 – Check the checkbox next to each subject you want to map to the selected class. Subjects already mapped will appear pre-checked.",
        "Step 3 – Click SAVE MAPPING to save the selected subject-to-class assignments.",
      ],
    },
    {
      title: "📊 Table Columns",
      items: [
        "S.No – Serial number of the subject in the list.",
        "Subject Name – The name of the subject available for mapping.",
        "Select – Checkbox to include or exclude the subject from the mapping for the selected class.",
      ],
    },
    {
      title: "⚙️ Actions",
      items: [
        "SAVE MAPPING – Saves all checked subject mappings for the selected class. At least one subject must be selected. Requires Add permission.",
      ],
    },
    {
      title: "🔐 Permissions",
      content:
        "The SAVE MAPPING button is disabled if you do not have Add permission. Contact your administrator if you need access.",
    },
  ];

  return (
    <Container maxWidth="md" sx={{ mb: 2 }}>
      <Box
        sx={{
          backgroundColor: '#0B1F40', // dark navy blue
          padding: '12px 24px',
          boxShadow: 3,
          borderRadius: '4px 4px 0 0',
          margin: '2px auto',
          maxWidth: "900px",
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          position: 'relative',
        }}
      >
        <Typography variant="h4" sx={{ color: '#fff', textAlign: 'center', fontWeight: 'bold' }}>
          Class Mapping
        </Typography>

        {/* Info Icon Button */}
        <Tooltip title="View page information & guide" arrow>
          <IconButton
            onClick={() => setShowInfoModal(true)}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.75)",
              backgroundColor: "rgba(255,255,255,0.1)",
              border: "1.5px solid rgba(255,255,255,0.25)",
              borderRadius: "50%",
              width: 30,
              height: 30,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "rgba(100,181,246,0.25)",
                borderColor: "#64B5F6",
                color: "#64B5F6",
                transform: "scale(1.1)",
              },
            }}
          >
            <InfoIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <StyledPaper elevation={3}>
        {loading && (
          <Fade in={loading}>
            <CircularProgress sx={{ display: "block", mx: "auto", mb: 2 }} />
          </Fade>
        )}

        <FormControl fullWidth sx={{ my: 2 }}>
          <InputLabel>Class</InputLabel>
          <Select
            value={selectedClassId}
            onChange={handleClassChange}
            label="Class"
            disabled={loading}
            // sx={{ borderRadius: 2 }}
          >
            <MenuItem value="">Select Class</MenuItem>
            {ddlClass.map((item) => (
              <MenuItem key={item.classId} value={item.classId}>
                {item.className}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </StyledPaper>

      {subjectMapping.length > 0 && (
        <Fade in={subjectMapping.length > 0}>
          <StyledPaper elevation={3} sx={{ mt: 3 }}>
            <TableContainer component={Paper}>
              <Table
                sx={{
                  minWidth: 650,
                  border: '1px solid #ccc',
                  '& .MuiTableCell-root': {
                    borderRight: '1px solid #ccc', // vertical line
                    fontSize: '1rem', // increased font size
                  },
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                      S.No
                    </TableCell>
                    <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                      Subject Name
                    </TableCell>
                    <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                      Select
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subjectMapping.map((subject, index) => (
                    <TableRow
                      key={subject.subjectId}
                      sx={{
                        borderTop: '1px solid #ccc',
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                          transition: "background-color 0.2s",
                        },
                      }}
                    >
                      <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>
                        {index + 1}
                      </TableCell>
                      <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>
                        {subject.subjectName}
                      </TableCell>
                      <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>
                        <Checkbox
                          checked={subject.checked}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            setSubjectMapping((prev) =>
                              prev.map((item) =>
                                item.subjectId === subject.subjectId
                                  ? { ...item, checked }
                                  : item
                              )
                            );
                          }}
                          disabled={loading}
                          sx={{ "&.Mui-checked": { color: "#1976d2" } }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              display="flex"
              justifyContent="center"
              mt={3} // Optional: Add margin-top if needed
            >
              <Tooltip title={!canAdd && "You are not Authorized to make changes"} arrow>
                <span>
                  <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    disabled={loading || !canAdd}
                    sx={{
                      width: "160px",
                      height: "50px",
                      padding: "10px 0",
                      fontWeight: 'bold',
                    }}
                  >
                    {loading ? "SAVING..." : "SAVE MAPPING"}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </StyledPaper>
        </Fade>
      )}

      {/* ===== Info Modal ===== */}
      <Modal
        open={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        aria-labelledby="info-modal-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: 520 },
            maxHeight: '85vh',
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: '0 8px 40px rgba(11,31,61,0.22)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              backgroundColor: '#0B1F3D',
              padding: '14px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon sx={{ color: '#64B5F6', fontSize: 22 }} />
              <Typography id="info-modal-title" variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
                Class Mapping — Page Guide
              </Typography>
            </Box>
            {/* Close Button */}
           
          </Box>

          {/* Modal Body — scrollable */}
          <Box sx={{ overflowY: 'auto', padding: '20px 24px', flex: 1 }}>
            {infoSections.map((section, idx) => (
              <Box key={idx} sx={{ mb: 2.5 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: '#0B1F3D',
                    mb: 0.8,
                    fontSize: '0.97rem',
                  }}
                >
                  {section.title}
                </Typography>

                {section.content && (
                  <Typography
                    variant="body2"
                    sx={{ color: '#444', lineHeight: 1.7, fontSize: '0.875rem' }}
                  >
                    {section.content}
                  </Typography>
                )}

                {section.items && (
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {section.items.map((item, i) => (
                      <Box
                        component="li"
                        key={i}
                        sx={{
                          fontSize: '0.875rem',
                          color: '#444',
                          lineHeight: 1.75,
                          mb: 0.3,
                        }}
                      >
                        {item}
                      </Box>
                    ))}
                  </Box>
                )}

                {idx < infoSections.length - 1 && (
                  <Divider sx={{ mt: 2, borderColor: 'rgba(11,31,61,0.08)' }} />
                )}
              </Box>
            ))}
          </Box>

          {/* Modal Footer */}
          <Box
            sx={{
              padding: '12px 24px',
              borderTop: '1px solid rgba(11,31,61,0.1)',
              display: 'flex',
              justifyContent: 'flex-end',
              backgroundColor: '#f9fafb',
            }}
          >
            <Button
              variant="contained"
              onClick={() => setShowInfoModal(false)}
              sx={{
                backgroundColor: '#0B1F3D',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { backgroundColor: '#1a3560' },
              }}
            >
              Got it
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default ClassMapping;